/**
 * Rapprochement TPS/TVQ — perçue (revenus dojo) vs payée (reçus WellyBox).
 * Prépare les remises trimestrielles à Revenu Québec en un coup d'œil.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Scales, ArrowDown, ArrowUp, Receipt, TrendUp, Info, Calculator, FilePdf, CalendarBlank } from "@phosphor-icons/react";

const money = (x) => `${Number(x || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
const NOW = new Date();
const YEARS = Array.from({ length: 8 }, (_, i) => NOW.getFullYear() - i);
const QUARTERS = [
  { id: "Q1", label: "T1 (jan–mar)", m: [0, 2] },
  { id: "Q2", label: "T2 (avr–juin)", m: [3, 5] },
  { id: "Q3", label: "T3 (juil–sep)", m: [6, 8] },
  { id: "Q4", label: "T4 (oct–déc)", m: [9, 11] },
  { id: "AN", label: "Année complète", m: [0, 11] },
];

function iso(d) { return d.toISOString().slice(0, 10); }
function rangeFor(year, q) {
  const def = QUARTERS.find((x) => x.id === q);
  const start = new Date(Date.UTC(year, def.m[0], 1));
  const end = new Date(Date.UTC(year, def.m[1] + 1, 0));
  return { start: iso(start), end: iso(end) };
}

function StatRow({ label, value, strong }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-zinc-800 last:border-0">
      <span className={`text-sm ${strong ? "text-zinc-200 font-medium" : "text-zinc-400"}`}>{label}</span>
      <span className={`tabular-nums ${strong ? "text-white font-semibold" : "text-zinc-300"}`}>{value}</span>
    </div>
  );
}

export default function RapprochementTPSTVQ() {
  const [year, setYear] = useState(NOW.getFullYear());
  const [quarter, setQuarter] = useState("Q1");
  const [ponderer, setPonderer] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = rangeFor(year, quarter);
      const r = await api.get(`/finance/tax/rapprochement?start=${start}&end=${end}&ponderer=${ponderer}`);
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement du rapprochement");
    }
    setLoading(false);
  }, [year, quarter, ponderer]);

  useEffect(() => { load(); }, [load]);

  // Tableau annuel — récap TPS/TVQ mois par mois
  const [monthly, setMonthly] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const loadMonthly = useCallback(async () => {
    setLoadingMonthly(true);
    try {
      const r = await api.get(`/finance/tax/mensuel?year=${year}&ponderer=${ponderer}`);
      setMonthly(r.data);
    } catch {
      toast.error("Erreur de chargement du tableau mensuel");
    }
    setLoadingMonthly(false);
  }, [year, ponderer]);
  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const [exporting, setExporting] = useState(false);
  async function exportPdf() {
    setExporting(true);
    try {
      const { start, end } = rangeFor(year, quarter);
      const r = await api.get(
        `/finance/tax/rapprochement/pdf?start=${start}&end=${end}&ponderer=${ponderer}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Échec de génération du PDF");
    }
    setExporting(false);
  }

  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  async function genQboDraft() {
    setDrafting(true);
    try {
      const { start, end } = rangeFor(year, quarter);
      const r = await api.post("/finance/tax/rapprochement/qbo-draft", { start, end, ponderer });
      setDraft(r.data.draft);
      toast.success("Brouillon d'écriture créé (non publié)");
    } catch {
      toast.error("Échec de génération du brouillon");
    }
    setDrafting(false);
  }

  const net = data?.net_a_remettre;
  const isRefund = net?.remboursement;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="rapprochement-page">
      <div className="flex items-center gap-2 text-amber-400">
        <Scales size={24} weight="fill" />
        <h1 className="text-2xl font-bold text-white">Rapprochement TPS/TVQ</h1>
      </div>
      <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
        TPS/TVQ <b className="text-zinc-200">perçue</b> sur les revenus du dojo vs <b className="text-zinc-200">payée</b> sur
        les reçus d'affaires (crédits CTI/RTI). Prépare ta remise trimestrielle à Revenu Québec.
      </p>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3 mt-6 bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="year-select"
          className="bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-sm text-zinc-100">
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex gap-1 flex-wrap">
          {QUARTERS.map((q) => (
            <button key={q.id} onClick={() => setQuarter(q.id)} data-testid={`quarter-${q.id}`}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                quarter === q.id ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
              {q.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 ml-auto cursor-pointer" data-testid="ponderer-toggle">
          <input type="checkbox" checked={ponderer} onChange={(e) => setPonderer(e.target.checked)}
            className="w-4 h-4 accent-amber-500" />
          <span className="text-xs text-zinc-300">Pondérer les crédits par la part Impact</span>
        </label>
        <button onClick={exportPdf} disabled={exporting || !data} data-testid="export-pdf-btn"
          className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-100 text-xs font-medium px-3 py-1.5 rounded transition-colors">
          <FilePdf size={15} /> {exporting ? "Génération…" : "Exporter en PDF"}
        </button>
        <button onClick={genQboDraft} disabled={drafting || !data} data-testid="qbo-draft-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5 rounded transition-colors">
          <Calculator size={15} /> {drafting ? "Création…" : "Écriture QBO (brouillon)"}
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-12 text-center">Calcul en cours…</div>
      ) : !data ? null : (
        <>
          {/* Net à remettre — carte vedette */}
          <div className={`mt-6 rounded-xl p-6 border ${isRefund ? "bg-green-950/40 border-green-800" : "bg-amber-950/30 border-amber-700"}`}
            data-testid="net-card">
            <div className="text-xs uppercase tracking-widest text-zinc-400">
              {isRefund ? "Remboursement estimé en ta faveur" : "Net à remettre à Revenu Québec"}
            </div>
            <div className={`text-4xl font-bold mt-1 ${isRefund ? "text-green-300" : "text-amber-300"}`} data-testid="net-total">
              {money(Math.abs(net.total))}
            </div>
            <div className="flex gap-6 mt-3 text-sm">
              <span className="text-zinc-300">TPS&nbsp;: <b className="text-white">{money(Math.abs(net.tps))}</b></span>
              <span className="text-zinc-300">TVQ&nbsp;: <b className="text-white">{money(Math.abs(net.tvq))}</b></span>
            </div>
          </div>

          {/* Deux colonnes */}
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5" data-testid="percue-card">
              <div className="flex items-center gap-2 text-green-400 mb-3">
                <ArrowDown size={18} weight="bold" />
                <h2 className="text-sm font-semibold uppercase tracking-wide">Perçue (sur revenus)</h2>
              </div>
              <StatRow label="TPS perçue" value={money(data.percue.tps)} />
              <StatRow label="TVQ perçue" value={money(data.percue.tvq)} />
              <StatRow label="Total taxe perçue" value={money(data.percue.total)} strong />
              <div className="mt-3 text-xs text-zinc-500 space-y-1">
                <div>Revenu brut période&nbsp;: <b className="text-zinc-300">{money(data.percue.revenu_brut)}</b></div>
                <div>Zen Planner&nbsp;: {money(data.percue.sources.zen_planner.taxe)} ({data.percue.sources.zen_planner.nb} paiements)</div>
                <div>Stripe/adhésions&nbsp;: {money(data.percue.sources.stripe_adhesions.taxe_estimee)} ({data.percue.sources.stripe_adhesions.nb})</div>
              </div>
            </div>

            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5" data-testid="payee-card">
              <div className="flex items-center gap-2 text-blue-400 mb-3">
                <ArrowUp size={18} weight="bold" />
                <h2 className="text-sm font-semibold uppercase tracking-wide">Payée (crédits CTI/RTI)</h2>
              </div>
              <StatRow label="TPS payée (crédit)" value={money(data.payee.tps)} />
              <StatRow label="TVQ payée (crédit)" value={money(data.payee.tvq)} />
              <StatRow label="Total crédits" value={money(data.payee.total)} strong />
              <div className="mt-2 text-xs text-zinc-500">
                {data.payee.nb_recus} reçu(s){ponderer ? ` · avant pondération : ${money(data.payee.tps_avant_ponderation + data.payee.tvq_avant_ponderation)}` : ""}
              </div>
              {data.payee.par_categorie?.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="payee-categories">
                  <div className="text-[11px] uppercase tracking-wide text-zinc-500">Top catégories</div>
                  {data.payee.par_categorie.map((c) => (
                    <div key={c.categorie} className="flex justify-between text-xs text-zinc-400">
                      <span>{c.categorie.replace(/_/g, " ")}</span>
                      <span className="tabular-nums">{money(c.credit)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Note */}
          <div className="mt-4 flex items-start gap-2 text-xs text-zinc-500 bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <Info size={16} className="flex-shrink-0 mt-0.5 text-amber-500" />
            <span>{data.note}</span>
          </div>

          {/* Brouillon d'écriture QBO */}
          {draft && (
            <div className="mt-4 border border-emerald-800 bg-emerald-950/30 rounded-xl p-5" data-testid="qbo-draft-card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-emerald-300 uppercase tracking-wide flex items-center gap-2">
                  <Calculator size={16} /> Écriture de remise — BROUILLON
                </div>
                <span className="text-[10px] uppercase bg-emerald-900 text-emerald-200 px-2 py-0.5 rounded-full">non publié</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase">
                    <th className="text-left font-medium pb-1">Compte</th>
                    <th className="text-left font-medium pb-1">Sens</th>
                    <th className="text-right font-medium pb-1">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.lignes.map((l, i) => (
                    <tr key={i} className="border-t border-emerald-900/50">
                      <td className="py-1.5 text-zinc-200">{l.compte}{l.tax_no ? <span className="text-zinc-500 text-xs"> · {l.tax_no}</span> : null}</td>
                      <td className="py-1.5 text-zinc-300">{l.type}</td>
                      <td className="py-1.5 text-right tabular-nums text-white">{money(l.montant)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-emerald-300/70 mt-3 flex items-start gap-1.5">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                Ce brouillon n'a PAS été publié dans QuickBooks. Fais-le valider par ton comptable, puis saisis-le (ou pousse-le) dans QBO.
              </p>
            </div>
          )}
        </>
      )}

      {/* ── TABLEAU ANNUEL — récap TPS/TVQ mois par mois ─────────────────── */}
      <div className="mt-8" data-testid="tax-monthly-section">
        <div className="flex items-center gap-2 text-amber-400 mb-1">
          <CalendarBlank size={20} weight="fill" />
          <h2 className="text-lg font-bold text-white">Tableau des taxes — mois par mois ({year})</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4 max-w-2xl">
          Récapitulatif de la TPS/TVQ perçue et payée pour chaque mois de l'année, pour préparer
          tes déclarations à Revenu Québec.
        </p>
        {loadingMonthly ? (
          <div className="text-zinc-500 py-8 text-center">Calcul du tableau mensuel…</div>
        ) : !monthly ? null : (
          <div className="overflow-x-auto bg-zinc-900/60 border border-zinc-800 rounded-xl">
            <table className="w-full text-sm" data-testid="tax-monthly-table">
              <thead>
                <tr className="text-zinc-400 text-xs uppercase border-b border-zinc-800">
                  <th className="text-left font-medium px-4 py-3">Mois</th>
                  <th className="text-right font-medium px-3 py-3">Revenu brut</th>
                  <th className="text-right font-medium px-3 py-3">TPS perçue</th>
                  <th className="text-right font-medium px-3 py-3">TVQ perçue</th>
                  <th className="text-right font-medium px-3 py-3">TPS payée</th>
                  <th className="text-right font-medium px-3 py-3">TVQ payée</th>
                  <th className="text-right font-medium px-4 py-3">Net à remettre</th>
                </tr>
              </thead>
              <tbody>
                {monthly.mois.map((m) => {
                  const empty = m.revenu_brut === 0 && m.tps_payee === 0 && m.tvq_payee === 0;
                  return (
                    <tr key={m.mois} data-testid={`tax-month-row-${m.mois}`}
                      className={`border-b border-zinc-800/60 last:border-0 ${empty ? "opacity-40" : ""}`}>
                      <td className="px-4 py-2.5 text-zinc-200">{m.mois_label}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-zinc-300">{money(m.revenu_brut)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-green-300">{money(m.tps_percue)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-green-300">{money(m.tvq_percue)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-blue-300">{money(m.tps_payee)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-blue-300">{money(m.tvq_payee)}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${m.net_total < 0 ? "text-green-400" : "text-amber-300"}`}>{money(m.net_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-zinc-800/50 font-semibold" data-testid="tax-monthly-total-row">
                  <td className="px-4 py-3 text-white uppercase text-xs tracking-wide">Total {year}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-white">{money(monthly.totaux.revenu_brut)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-green-300">{money(monthly.totaux.tps_percue)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-green-300">{money(monthly.totaux.tvq_percue)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-blue-300">{money(monthly.totaux.tps_payee)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-blue-300">{money(monthly.totaux.tvq_payee)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${monthly.totaux.net_total < 0 ? "text-green-400" : "text-amber-300"}`}>{money(monthly.totaux.net_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
